/**
 * Behaviour for [data-pag-panel] drawers (snippets/pag-panel.liquid).
 *
 * Delegated, so a panel works whether it was in the page at load or inserted
 * later. The native dialog provides focus containment and Esc; this adds the
 * slide/fade in both directions (Esc is intercepted so closing animates too),
 * backdrop click, Horizon's shared scroll lock (which knows about the 990px
 * .page-wrapper scroller), and focus return to whatever opened the panel.
 */

import { lockScroll, unlockScroll } from '@theme/utilities';

const CLOSE_FALLBACK_MS = 400;
const openers = new WeakMap();

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @param {string | HTMLDialogElement} target */
function resolve(target) {
  const dialog = typeof target === 'string' ? document.getElementById(target) : target;
  return dialog instanceof HTMLDialogElement && dialog.hasAttribute('data-pag-panel') ? dialog : null;
}

/**
 * @param {string | HTMLDialogElement} target
 * @param {Element | null} [opener]
 */
export function openPanel(target, opener = document.activeElement) {
  const dialog = resolve(target);
  if (!dialog || dialog.open) return;

  openers.set(dialog, opener instanceof HTMLElement ? opener : null);
  dialog.classList.remove('is-closing');
  dialog.showModal();
  lockScroll(dialog);

  // One frame at the closed position, so the transition has a start state.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => dialog.classList.add('is-open'));
  });

  dialog.dispatchEvent(new CustomEvent('pag:panel:open', { bubbles: true }));
}

/** @param {string | HTMLDialogElement} target */
export function closePanel(target) {
  const dialog = resolve(target);
  if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;

  const finish = () => {
    dialog.classList.remove('is-closing');
    dialog.close();
    unlockScroll(dialog);
    openers.get(dialog)?.focus({ preventScroll: true });
    dialog.dispatchEvent(new CustomEvent('pag:panel:close', { bubbles: true }));
  };

  dialog.classList.remove('is-open');

  if (reducedMotion()) {
    finish();
    return;
  }

  dialog.classList.add('is-closing');
  const sheet = dialog.querySelector('.pag-panel__sheet');
  let done = false;
  const once = () => {
    if (done) return;
    done = true;
    finish();
  };
  sheet?.addEventListener('transitionend', once, { once: true });
  // transitionend never fires if the transition is interrupted or disabled.
  setTimeout(once, CLOSE_FALLBACK_MS);
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const opener = event.target.closest('[data-pag-panel-open]');
  if (opener) {
    event.preventDefault();
    openPanel(opener.getAttribute('data-pag-panel-open'), opener);
    return;
  }

  const closer = event.target.closest('[data-pag-panel-close]');
  if (closer) {
    closePanel(closer.closest('[data-pag-panel]'));
    return;
  }

  // A click on the dialog element itself is a click outside the sheet.
  if (event.target instanceof HTMLDialogElement && event.target.hasAttribute('data-pag-panel')) {
    closePanel(event.target);
  }
});

document.addEventListener(
  'cancel',
  (event) => {
    const dialog = resolve(/** @type {HTMLDialogElement} */ (event.target));
    if (!dialog) return;
    event.preventDefault();
    closePanel(dialog);
  },
  true
);
